import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { PLANS } from "../../constants/testIds";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FormField } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";

function todayIso() { return new Date().toISOString().slice(0, 10); }

// Owner Cockpit's promotion create — same `rules: { discount_percent }` shape the Manager's
// already-working "Local offers" screen uses (see `pages/desk/mgr/LocalOffers.jsx`), just with
// an optional multi-branch picker: `routers/promotions.py` only force-pins `branch_scope` to the
// caller's own branch for a Manager — a Super Admin's own submitted `branch_scope` (empty =
// org-wide) is honored as-is.
export function PromotionFormDialog({ open, onOpenChange, branches }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState("");
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(todayIso());
  const [branchScope, setBranchScope] = useState([]);
  const [error, setError] = useState("");

  const toggleBranch = (id) => setBranchScope((s) => (s.includes(id) ? s.filter((b) => b !== id) : [...s, id]));

  const mutation = useMutation({
    mutationFn: () => api.post("/promotions", {
      code, rules: { discount_percent: Number(discount) },
      valid_window: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` },
      branch_scope: branchScope.length ? branchScope : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promotions-list"] });
      setCode(""); setDiscount(""); setBranchScope([]);
      onOpenChange(false);
    },
    onError: (e) => setError(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={PLANS.promoFormDialog}>
        <DialogHeader>
          <DialogTitle>Add promotion</DialogTitle>
          <DialogDescription>Leave branches unchecked for an org-wide offer.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={PLANS.promoFormError}>{error}</p>}
        <div className="space-y-3">
          <FormField label="Offer code" required testId={PLANS.promoCodeInput} value={code} onChange={(v) => setCode(v.toUpperCase())} placeholder="e.g. FESTIVE10" />
          <FormField label="Discount %" required type="number" testId={PLANS.promoDiscountInput} value={discount} onChange={setDiscount} />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Valid from</Label><Input data-testid={PLANS.promoStartInput} type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div><Label>Valid to</Label><Input data-testid={PLANS.promoEndInput} type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Branches</Label>
            <div className="flex flex-col gap-2 rounded-[10px] p-3" style={{ border: "1px solid var(--scarlet-hairline)" }}>
              {branches.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={PLANS.promoBranchCheckbox(b.id)} checked={branchScope.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} />
                  {b.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <SubmitButton
            testId={PLANS.promoFormSubmit} disabled={!code.trim() || !discount} pending={mutation.isPending}
            onClick={() => { setError(""); mutation.mutate(); }} label="Add promotion"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
